import { FC, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import {
  Box,
  Button,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { SwitchInput } from "@framework/mui-inputs-core";

import { ALARM_COLOR_KEYS, DEFAULT_USER_ALARM_COLORS, type AlarmColorCategory } from "../../../utils/alarm-colors";

import { IProfileFormValues } from "../types";

const ALARM_COLOR_ROWS: { key: AlarmColorCategory; labelId: string }[] = [
  { key: "notDefined", labelId: "pages.profile.alarmColorNotDefined" },
  { key: "noData", labelId: "pages.profile.alarmColorNoData" },
  { key: "normal", labelId: "pages.profile.alarmColorNormal" },
  { key: "attention", labelId: "pages.profile.alarmColorAttention" },
  { key: "warning", labelId: "pages.profile.alarmColorWarning" },
  { key: "alarm", labelId: "pages.profile.alarmColorAlarm" },
  { key: "invalid", labelId: "pages.profile.alarmColorInvalid" },
  { key: "pickupFailed", labelId: "pages.profile.alarmColorPickupFailed" },
];

function toHexColorInputValue(raw: string | undefined, fallbackHex: string): string {
  const v = (raw ?? "").trim();
  if (/^#[0-9A-Fa-f]{6}$/i.test(v)) {
    return v.toLowerCase();
  }
  if (/^[0-9A-Fa-f]{6}$/i.test(v)) {
    return `#${v.toLowerCase()}`;
  }
  const fb = fallbackHex.startsWith("#") ? fallbackHex : `#${fallbackHex}`;
  return fb.toLowerCase();
}

export const AlarmColorsSettings: FC<{ hideTitle?: boolean }> = ({ hideTitle = false }) => {
  const { formatMessage } = useIntl();
  const { setValue, control, getValues } = useFormContext<IProfileFormValues>();
  const watchedUseDefault = useWatch({ control, name: "useDefaultAlarmColors" });
  const useDefaultResolved = watchedUseDefault ?? getValues("useDefaultAlarmColors") ?? true;
  const showCustomPaletteTable = useDefaultResolved === false;

  const handleResetPalette = useCallback(() => {
    ALARM_COLOR_KEYS.forEach(key => {
      setValue(`alarmColors.${key}`, DEFAULT_USER_ALARM_COLORS[key], { shouldDirty: true, shouldValidate: false });
    });
  }, [setValue]);

  return (
    <Box sx={{ mt: hideTitle ? 0 : 2 }}>
      {!hideTitle ? (
        <Typography variant="h6" gutterBottom>
          <FormattedMessage id="pages.profile.titleAlarmColors" />
        </Typography>
      ) : null}
      <SwitchInput
        name="useDefaultAlarmColors"
        label={<FormattedMessage id="pages.profile.useDefaultAlarmColors" />}
        onChange={(_event, checked) => {
          setValue("useDefaultAlarmColors", checked, { shouldDirty: true, shouldValidate: false });
          if (checked) {
            handleResetPalette();
          }
        }}
      />
      <Collapse in={showCustomPaletteTable} timeout="auto" unmountOnExit={false}>
        <Box sx={{ mt: 2 }}>
          <Button type="button" variant="outlined" size="small" onClick={handleResetPalette} sx={{ mb: 2 }}>
            <FormattedMessage id="pages.profile.restoreAlarmColorDefaults" />
          </Button>
          <Table size="small" sx={{ maxWidth: 560, border: t => `1px solid ${t.palette.divider}` }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <FormattedMessage id="pages.profile.alarmColorColumnState" />
                </TableCell>
                <TableCell align="right">
                  <FormattedMessage id="pages.profile.alarmColorColumnColor" />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ALARM_COLOR_ROWS.map(({ key, labelId }) => {
                const fallbackForPicker = DEFAULT_USER_ALARM_COLORS[key];
                return (
                  <TableRow key={key}>
                    <TableCell>
                      <FormattedMessage id={labelId} />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                        <Controller
                          name={`alarmColors.${key}`}
                          control={control}
                          render={({ field }) => {
                            const colorInputValue = toHexColorInputValue(field.value, fallbackForPicker);
                            return (
                              <>
                                <Box
                                  component="label"
                                  title={formatMessage({ id: "pages.profile.alarmColorPickPalette" })}
                                >
                                  <input
                                    type="color"
                                    value={colorInputValue}
                                    aria-label={formatMessage({ id: "pages.profile.alarmColorPickPalette" })}
                                    onChange={e => {
                                      const v = e.target.value;
                                      setValue(`alarmColors.${key}`, v, {
                                        shouldDirty: true,
                                        shouldTouch: true,
                                        shouldValidate: false,
                                      });
                                    }}
                                    style={{
                                      width: 36,
                                      height: 36,
                                      border: "none",
                                      cursor: "pointer",
                                      background: "none",
                                    }}
                                  />
                                </Box>
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  sx={{ width: 100, my: 0 }}
                                  value={field.value ?? ""}
                                  name={field.name}
                                  inputRef={field.ref}
                                  onChange={e => {
                                    const v = e.target.value;
                                    setValue(`alarmColors.${key}`, v, {
                                      shouldDirty: true,
                                      shouldTouch: true,
                                      shouldValidate: false,
                                    });
                                  }}
                                  onBlur={field.onBlur}
                                  inputProps={{ maxLength: 7 }}
                                />
                              </>
                            );
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Collapse>
    </Box>
  );
};
